// 管理者: パフォーマー管理(設計書 §3.5.3)

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, apiRequestRaw } from '../client';

export type Plan = 'free' | 'pro';
export type PerformerStatus = 'pending' | 'active' | 'disabled';

export interface PerformerListItem {
  ulid: string;
  email: string;
  display_name: string;
  plan: Plan;
  status: PerformerStatus;
  current_session_state: string | null;
  last_login_at: string | null;
  total_streaming_seconds: number;
  created_at: string;
}

export interface PendingPerformer {
  ulid: string;
  email: string;
  display_name: string;
  applied_at: string;
}

export interface MosaicSettings {
  mosaic_target_genital: boolean;
  mosaic_target_breast: boolean;
  detection_model: 'system_default' | 'user_custom';
  training_data_dir: string | null;
  output_width: number;
  output_height: number;
  output_framerate: number;
  output_bitrate_kbps: number;
  output_quality: 'low' | 'standard' | 'high';
  output_url_override: string | null;
  stream_key_override_set: boolean;
}

export interface PerformerDetail {
  ulid: string;
  email: string;
  display_name: string;
  plan: Plan;
  status: PerformerStatus;
  approved_at: string | null;
  approved_by_admin: { ulid: string; name: string } | null;
  disabled_at: string | null;
  disabled_reason: string | null;
  google_linked: boolean;
  mosaic_settings: MosaicSettings | null;
  stats: {
    total_streaming_seconds: number;
    total_session_count: number;
    current_session_state: string | null;
  };
  current_session: {
    ulid: string;
    server_name: string;
    slot: 'A' | 'B';
    started_at: string;
  } | null;
  created_at: string;
  last_login_at: string | null;
}

export interface PerformersListQuery {
  page?: number;
  per_page?: number;
  q?: string;
  plan?: Plan;
  status?: PerformerStatus;
}

export interface PerformersListResponse {
  data: PerformerListItem[];
  pagination: {
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  };
}

export const adminPerformerKeys = {
  all: ['admin', 'performers'] as const,
  list: (q: PerformersListQuery) => ['admin', 'performers', 'list', q] as const,
  pending: ['admin', 'performers', 'pending'] as const,
  detail: (ulid: string) => ['admin', 'performers', 'detail', ulid] as const,
};

export function usePendingPerformers() {
  return useQuery({
    queryKey: adminPerformerKeys.pending,
    queryFn: () => apiRequest<PendingPerformer[]>('/admin/performers/pending'),
  });
}

export function usePerformersList(params: PerformersListQuery) {
  return useQuery({
    queryKey: adminPerformerKeys.list(params),
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (params.page) qs.set('page', String(params.page));
      if (params.per_page) qs.set('per_page', String(params.per_page));
      if (params.q) qs.set('q', params.q);
      if (params.plan) qs.set('plan', params.plan);
      if (params.status) qs.set('status', params.status);
      const path = qs.toString() ? `/admin/performers?${qs.toString()}` : '/admin/performers';
      return await apiRequestRaw<PerformersListResponse>(path);
    },
    placeholderData: (prev) => prev,
  });
}

export function usePerformerDetail(ulid: string | null) {
  return useQuery({
    queryKey: ulid ? adminPerformerKeys.detail(ulid) : ['admin', 'performers', 'detail', 'noop'],
    queryFn: () => apiRequest<PerformerDetail>(`/admin/performers/${ulid}`),
    enabled: ulid !== null,
  });
}

function invalidatePerformer(qc: ReturnType<typeof useQueryClient>, ulid?: string) {
  void qc.invalidateQueries({ queryKey: adminPerformerKeys.all });
  void qc.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
  if (ulid) {
    void qc.invalidateQueries({ queryKey: adminPerformerKeys.detail(ulid) });
  }
}

export function useApprovePerformer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ulid: string) =>
      apiRequest<void>(`/admin/performers/${ulid}/approve`, { method: 'POST' }),
    onSuccess: (_data, ulid) => invalidatePerformer(qc, ulid),
  });
}

export function useRejectPerformer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ulid: string) =>
      apiRequest<void>(`/admin/performers/${ulid}/reject`, { method: 'POST' }),
    onSuccess: (_data, ulid) => invalidatePerformer(qc, ulid),
  });
}

export interface UpdatePerformerInput {
  ulid: string;
  body: { plan?: Plan; display_name?: string };
}

export function useUpdatePerformer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ulid, body }: UpdatePerformerInput) =>
      apiRequest<void>(`/admin/performers/${ulid}`, { method: 'PATCH', body }),
    onSuccess: (_data, vars) => invalidatePerformer(qc, vars.ulid),
  });
}

export interface UpdateMosaicSettingsInput {
  ulid: string;
  body: Partial<Omit<MosaicSettings, 'stream_key_override_set'>>;
}

export function useUpdateMosaicSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ulid, body }: UpdateMosaicSettingsInput) =>
      apiRequest<void>(`/admin/performers/${ulid}/mosaic-settings`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: (_data, vars) => invalidatePerformer(qc, vars.ulid),
  });
}

export function useDisablePerformer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ulid, reason }: { ulid: string; reason: string }) =>
      apiRequest<void>(`/admin/performers/${ulid}/disable`, {
        method: 'POST',
        body: { reason },
      }),
    onSuccess: (_data, vars) => invalidatePerformer(qc, vars.ulid),
  });
}

export function useEnablePerformer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ulid: string) =>
      apiRequest<void>(`/admin/performers/${ulid}/enable`, { method: 'POST' }),
    onSuccess: (_data, ulid) => invalidatePerformer(qc, ulid),
  });
}

export function useDeletePerformer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ulid: string) =>
      apiRequest<void>(`/admin/performers/${ulid}`, { method: 'DELETE' }),
    onSuccess: () => invalidatePerformer(qc),
  });
}

export const PERFORMER_STATUS_LABELS: Record<PerformerStatus, string> = {
  pending: '承認待ち',
  active: '有効',
  disabled: '無効',
};
